import {
  displayNotification,
  ICharacter,
  NotificationDurationMs,
  NotificationType,
  Screen,
  useApp,
  useAppDispatch,
  useUserSettings,
} from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { CharacterCardActions } from "@next-platform/client-common-spicy-ui";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import useApi from "@/components/useApi";
import { CHATBOT_INTERACTION_EVENT_MAP, ROUTES } from "@/constants";
import { useRecombee } from "@/hooks";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import { CharacterRatingWeights } from "@/pages/Chat/components/constants";
import { removeRecommendationFromLocalStorage } from "@/utils";

import { CharacterCardDropdownActions } from "../../types";

interface IUseCharacterActionsProps {
  charHits: ICharacter[] | null;
  nsfwToggle: boolean;
  screenName: Screen;
  likedBots?: string[];
  onUserLikebot: (id: string, screenName: Screen) => Promise<string[]>;
}

// eslint-disable-next-line import/prefer-default-export, max-lines-per-function -- Named export consumed across multiple files; converting to default export would require updating all call sites. Hook manages multiple character card actions and exceeds function line limit; refactor tracked as a follow-up
export const useCharacterActions = ({ charHits, nsfwToggle, screenName, likedBots, onUserLikebot }: IUseCharacterActionsProps) => {
  const isCharacterShareLinkAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.CharacterShareLink);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { logEvent } = useQueueLogEvent();
  const { onUserBlockCreator } = useUserSettings();
  const { recombeeRecommendationsConfig } = useApp();
  const {
    homepageRecommendationsFetchCount,
    homepageRecommendationsDislikedAllowedToRemoveCount,
    recommendationspageFetchCount,
    recommendationspageDislikedAllowedToRemoveCount,
  } = recombeeRecommendationsConfig ?? {};
  const { recombeeUserId, sendUserInteractionAddBookmark, sendUserInteractionDetailView, sendUserInteractionRating } = useRecombee();
  const { callApi, jsonApiService } = useApi();

  const [stateCharacterInfo, setStateCharacterInfo] = useState<ICharacter | null>(null);
  const [stateIsShareModalVisible, setStateIsShareModalVisible] = useState(false);
  const [stateIsBlockModalVisible, setStateIsBlockModalVisible] = useState(false);
  const [stateIsDeleteModalVisible, setStateIsDeleteModalVisible] = useState(false);
  const [stateDeleteCharacterId, setStateDeleteCharacterId] = useState<string | null>(null);
  const [stateDislikedRecommendedCharacterIds, setStateDislikedRecommendedCharacterIds] = useState<string[]>([]);
  const [stateIsDeleting, setStateIsDeleting] = useState(false);

  const handleFavorite = useCallback(
    async (characterId: string) => {
      const isLiked = !likedBots?.includes(characterId);
      await onUserLikebot(characterId, screenName);
      if (isLiked) {
        await sendUserInteractionAddBookmark(recombeeUserId, characterId);
      }
    },
    [likedBots, onUserLikebot, screenName, recombeeUserId, sendUserInteractionAddBookmark],
  );

  const handleCharacterInfo = useCallback(
    (charId: string) => {
      const characterId = stateCharacterInfo?.character_id || stateCharacterInfo?.id;
      let charData;
      if (characterId !== charId) {
        charData = charHits?.find((char) => char?.character_id === charId || char?.id === charId);
        setStateCharacterInfo(charData ?? null);
      }
      return charData || stateCharacterInfo;
    },
    [stateCharacterInfo, charHits],
  );

  const handleCharacterChatInteraction = useCallback(
    (characterId: string) => {
      const charData = handleCharacterInfo(characterId);
      void sendUserInteractionDetailView(recombeeUserId, characterId, {
        recommId: charData?.recommId,
      });
    },
    [handleCharacterInfo, recombeeUserId, sendUserInteractionDetailView],
  );

  const handleLessLikeThis = useCallback(
    (characterId: string, userId: string) => {
      const charData = handleCharacterInfo(characterId);
      if (charData?.recommId) {
        setStateDislikedRecommendedCharacterIds((prev) => [...prev, characterId]);
        removeRecommendationFromLocalStorage({
          characterId,
          nsfwToggle,
          homepageRecommendationsFetchCount,
          homepageRecommendationsDislikedAllowedToRemoveCount,
          recommendationspageFetchCount,
          recommendationspageDislikedAllowedToRemoveCount,
        });
      }
      void sendUserInteractionRating(userId, characterId, CharacterRatingWeights.DISLIKE);
    },
    [
      handleCharacterInfo,
      nsfwToggle,
      homepageRecommendationsFetchCount,
      homepageRecommendationsDislikedAllowedToRemoveCount,
      recommendationspageFetchCount,
      recommendationspageDislikedAllowedToRemoveCount,
      sendUserInteractionRating,
    ],
  );

  const handleDeleteCharacter = useCallback((characterId: string) => {
    setStateDeleteCharacterId(characterId);
    setStateIsDeleteModalVisible(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!stateDeleteCharacterId || stateIsDeleting) return;

    setStateIsDeleting(true);

    try {
      await callApi(jsonApiService.deleteResource, `characters/${stateDeleteCharacterId}`);

      dispatch(
        displayNotification({
          message: "Chatbot deleted successfully",
          type: NotificationType.success,
          duration: NotificationDurationMs.normal,
        }),
      );

      setStateIsDeleteModalVisible(false);
      setStateDeleteCharacterId(null);

      return stateDeleteCharacterId;
    } catch {
      dispatch(
        displayNotification({
          message: "Failed to delete chatbot. Please try again.",
          type: NotificationType.error,
          duration: NotificationDurationMs.long,
        }),
      );
    } finally {
      setStateIsDeleting(false);
    }
  }, [stateDeleteCharacterId, stateIsDeleting, callApi, jsonApiService, dispatch]);

  const blockThisCreator = useCallback(async () => {
    if (stateCharacterInfo?.creator_user_id) {
      await onUserBlockCreator(stateCharacterInfo.creator_user_id);
    }
  }, [stateCharacterInfo, onUserBlockCreator]);

  const characterCardActionsHandler = useCallback(
    async (action: CharacterCardActions | CharacterCardDropdownActions, characterId: string, currentCategory: string, userId?: string | null) => {
      if (action === CharacterCardDropdownActions.SHARE && !isCharacterShareLinkAllowed) return;

      const eventName = CHATBOT_INTERACTION_EVENT_MAP[action as keyof typeof CHATBOT_INTERACTION_EVENT_MAP];
      if (eventName) {
        logEvent(eventName, {
          bot_category: currentCategory,
          character_id: characterId,
          page_name: screenName,
        });
      }

      switch (action) {
        case CharacterCardActions.CHAT:
          return handleCharacterChatInteraction(characterId);
        case CharacterCardActions.FAVORITE:
          return await handleFavorite(characterId);
        case CharacterCardDropdownActions.SHARE:
          handleCharacterInfo(characterId);
          return setStateIsShareModalVisible(true);
        case CharacterCardDropdownActions.BLOCK:
          handleCharacterInfo(characterId);
          return setStateIsBlockModalVisible(true);
        case CharacterCardDropdownActions.REPORT:
          return navigate(`${ROUTES.report}/${characterId}`);
        case CharacterCardDropdownActions.LESS_LIKE_THIS:
          if (userId) {
            return handleLessLikeThis(characterId, userId);
          }
          break;
      }
    },
    [
      logEvent,
      screenName,
      handleCharacterChatInteraction,
      handleFavorite,
      handleCharacterInfo,
      navigate,
      handleLessLikeThis,
      isCharacterShareLinkAllowed,
    ],
  );

  return {
    characterInfo: stateCharacterInfo,
    shareModalVisible: stateIsShareModalVisible,
    blockModalVisible: stateIsBlockModalVisible,
    deleteModalVisible: stateIsDeleteModalVisible,
    deleteCharacterId: stateDeleteCharacterId,
    dislikedRecommendedCharacterIds: stateDislikedRecommendedCharacterIds,
    setShareModalVisible: setStateIsShareModalVisible,
    setBlockModalVisible: setStateIsBlockModalVisible,
    setDeleteModalVisible: setStateIsDeleteModalVisible,
    handleDeleteCharacter,
    confirmDelete,
    blockThisCreator,
    characterCardActionsHandler,
    isDeleteLoading: stateIsDeleting,
  };
};
